<template>
  <div class="page-wrapper">
    <HeaderComponent />

    <main class="main-content">
      <!-- Page Hero -->
      <section class="page-hero">
        <div class="container">
          <div class="hero-content">
            <nav class="breadcrumb-nav">
              <a href="/" class="breadcrumb-link">Home</a>
              <span class="breadcrumb-separator">/</span>
              <span class="breadcrumb-current">Property Services</span>
            </nav>
            <h1 class="page-title">Property Self Service</h1>
            <p class="page-subtitle">Update your property information and keep your records current</p>
          </div>

          <!-- Progress Steps -->
          <div class="progress-stepper">
            <div
                v-for="(step, index) in steps"
                :key="index"
                class="step-item"
                :class="{
                active: currentStep === index + 1,
                completed: currentStep > index + 1
              }"
            >
              <div class="step-connector" v-if="index > 0"></div>
              <div class="step-circle">
                <svg v-if="currentStep > index + 1" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="3">
                  <polyline points="20 6 9 17 4 12"></polyline>
                </svg>
                <span v-else>{{ index + 1 }}</span>
              </div>
              <span class="step-label">{{ step }}</span>
            </div>
          </div>
        </div>
      </section>

      <!-- Main Content -->
      <section class="content-section">
        <div class="container">
          <div class="content-grid">
            <!-- Left Side - Info Panel -->
            <div class="info-panel">
              <div class="info-card">
                <div class="info-card-header">
                  <div class="info-icon">
                    <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5">
                      <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"></path>
                      <polyline points="9 22 9 12 15 12 15 22"></polyline>
                    </svg>
                  </div>
                  <h3>Property Updates</h3>
                </div>
                <p class="info-description">
                  Update your property details in the county valuation roll. This service allows property owners to submit changes for ownership, address, and other property information.
                </p>

                <div class="info-features">
                  <div class="feature-item">
                    <div class="feature-icon green">
                      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                        <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"></path>
                      </svg>
                    </div>
                    <div>
                      <strong>Secure Process</strong>
                      <p>Identity verification required</p>
                    </div>
                  </div>

                  <div class="feature-item">
                    <div class="feature-icon blue">
                      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                        <circle cx="12" cy="12" r="10"></circle>
                        <polyline points="12 6 12 12 16 14"></polyline>
                      </svg>
                    </div>
                    <div>
                      <strong>Quick Processing</strong>
                      <p>3-5 business days</p>
                    </div>
                  </div>

                  <div class="feature-item">
                    <div class="feature-icon purple">
                      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                        <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"></path>
                        <polyline points="22,6 12,13 2,6"></polyline>
                      </svg>
                    </div>
                    <div>
                      <strong>Status Updates</strong>
                      <p>SMS & email notifications</p>
                    </div>
                  </div>
                </div>

                <!-- Required Documents -->
                <div class="required-docs">
                  <h4>
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                      <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"></path>
                      <polyline points="14 2 14 8 20 8"></polyline>
                    </svg>
                    Required Documents
                  </h4>
                  <ul>
                    <li>
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                        <polyline points="20 6 9 17 4 12"></polyline>
                      </svg>
                      National ID or Passport
                    </li>
                    <li>
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                        <polyline points="20 6 9 17 4 12"></polyline>
                      </svg>
                      Title Deed or Sale Agreement
                    </li>
                    <li>
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                        <polyline points="20 6 9 17 4 12"></polyline>
                      </svg>
                      KRA PIN Certificate
                    </li>
                    <li>
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                        <polyline points="20 6 9 17 4 12"></polyline>
                      </svg>
                      Supporting Documents
                    </li>
                  </ul>
                  <p class="docs-note">PDF, JPG, PNG formats • Max 5MB per file</p>
                </div>

                <!-- Help Contact -->
                <div class="help-contact">
                  <h4>
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                      <circle cx="12" cy="12" r="10"></circle>
                      <path d="M9.09 9a3 3 0 0 1 5.83 1c0 2-3 3-3 3"></path>
                      <line x1="12" y1="17" x2="12.01" y2="17"></line>
                    </svg>
                    Need Help?
                  </h4>
                  <div class="contact-info">
                    <div class="contact-item">
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                        <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z"></path>
                      </svg>
                      <span>0700-000-000</span>
                    </div>
                    <div class="contact-item">
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                        <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"></path>
                        <polyline points="22,6 12,13 2,6"></polyline>
                      </svg>
                      <span>property@county.go.ke</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <!-- Right Side - Form Panel -->
            <div class="form-panel">
              <!-- Step 1: Property Search -->
              <transition name="slide-fade" mode="out-in">
                <div v-if="currentStep === 1" key="step1" class="form-card">
                  <div class="form-card-header">
                    <div class="step-badge">
                      <span class="step-number">1</span>
                      <span class="step-text">Search Property</span>
                    </div>
                  </div>

                  <div class="form-card-body">
                    <p class="form-intro">
                      Enter your property details to find your property in the valuation roll.
                    </p>

                    <div class="search-form">
                      <!-- Search Method Toggle -->
                      <div class="search-method-toggle">
                        <button
                            class="toggle-btn"
                            :class="{ active: searchMethod === 'plotNumber' }"
                            @click="searchMethod = 'plotNumber'"
                        >
                          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                            <rect x="3" y="3" width="18" height="18" rx="2" ry="2"></rect>
                            <line x1="3" y1="9" x2="21" y2="9"></line>
                            <line x1="9" y1="21" x2="9" y2="9"></line>
                          </svg>
                          By Plot Number
                        </button>
                        <button
                            class="toggle-btn"
                            :class="{ active: searchMethod === 'ownerDetails' }"
                            @click="searchMethod = 'ownerDetails'"
                        >
                          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                            <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"></path>
                            <circle cx="12" cy="7" r="4"></circle>
                          </svg>
                          By Owner Details
                        </button>
                      </div>

                      <!-- Plot Number Search -->
                      <div v-if="searchMethod === 'plotNumber'" class="search-fields">
                        <div class="form-group">
                          <label class="form-label">
                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                              <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"></path>
                              <circle cx="12" cy="10" r="3"></circle>
                            </svg>
                            Sub County
                          </label>
                          <div class="select-wrapper">
                            <select v-model="searchData.subCounty" class="form-select">
                              <option value="">-- Select Sub County --</option>
                              <option v-for="sc in subCounties" :key="sc.id" :value="sc">
                                {{ sc.subCountyName }}
                              </option>
                            </select>
                            <span class="select-icon">
                              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                                <polyline points="6 9 12 15 18 9"></polyline>
                              </svg>
                            </span>
                          </div>
                        </div>

                        <div class="form-group">
                          <label class="form-label">
                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                              <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"></path>
                              <circle cx="12" cy="10" r="3"></circle>
                            </svg>
                            Ward
                          </label>
                          <div class="select-wrapper">
                            <select v-model="searchData.ward" class="form-select" :disabled="!searchData.subCounty">
                              <option value="">-- Select Ward --</option>
                              <option v-for="ward in wards" :key="ward.id" :value="ward">
                                {{ ward.wardName }}
                              </option>
                            </select>
                            <span class="select-icon">
                              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                                <polyline points="6 9 12 15 18 9"></polyline>
                              </svg>
                            </span>
                          </div>
                        </div>

                        <div class="form-group">
                          <label class="form-label">
                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                              <rect x="3" y="3" width="18" height="18" rx="2" ry="2"></rect>
                            </svg>
                            Plot/LR Number
                          </label>
                          <div class="input-with-icon">
                            <input
                                v-model="searchData.plotNumber"
                                type="text"
                                class="form-input"
                                placeholder="e.g., LR/1234/5678"
                            />
                            <span class="input-icon">
                              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                                <rect x="3" y="3" width="18" height="18" rx="2" ry="2"></rect>
                              </svg>
                            </span>
                          </div>
                        </div>
                      </div>

                      <!-- Owner Details Search -->
                      <div v-else class="search-fields">
                        <div class="form-group">
                          <label class="form-label">
                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                              <rect x="3" y="4" width="18" height="18" rx="2" ry="2"></rect>
                              <line x1="16" y1="2" x2="16" y2="6"></line>
                              <line x1="8" y1="2" x2="8" y2="6"></line>
                              <line x1="3" y1="10" x2="21" y2="10"></line>
                            </svg>
                            ID Number
                          </label>
                          <div class="input-with-icon">
                            <input
                                v-model="searchData.idNumber"
                                type="text"
                                class="form-input"
                                placeholder="Enter ID Number"
                            />
                            <span class="input-icon">
                              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                                <rect x="3" y="4" width="18" height="18" rx="2" ry="2"></rect>
                              </svg>
                            </span>
                          </div>
                        </div>

                        <div class="form-group">
                          <label class="form-label">
                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                              <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"></path>
                              <circle cx="12" cy="7" r="4"></circle>
                            </svg>
                            Owner Name
                          </label>
                          <div class="input-with-icon">
                            <input
                                v-model="searchData.ownerName"
                                type="text"
                                class="form-input"
                                placeholder="Enter owner's full name"
                            />
                            <span class="input-icon">
                              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                                <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"></path>
                                <circle cx="12" cy="7" r="4"></circle>
                              </svg>
                            </span>
                          </div>
                        </div>

                        <div class="form-group">
                          <label class="form-label">
                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                              <rect x="5" y="2" width="14" height="20" rx="2" ry="2"></rect>
                              <line x1="12" y1="18" x2="12.01" y2="18"></line>
                            </svg>
                            Phone Number
                          </label>
                          <div class="input-with-icon">
                            <input
                                v-model="searchData.phoneNumber"
                                type="tel"
                                class="form-input"
                                placeholder="e.g., 0712345678"
                            />
                            <span class="input-icon">
                              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                                <rect x="5" y="2" width="14" height="20" rx="2" ry="2"></rect>
                                <line x1="12" y1="18" x2="12.01" y2="18"></line>
                              </svg>
                            </span>
                          </div>
                        </div>
                      </div>

                      <!-- Error Message -->
                      <div v-if="errorMessage" class="alert alert-error">
                        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                          <circle cx="12" cy="12" r="10"></circle>
                          <line x1="12" y1="8" x2="12" y2="12"></line>
                          <line x1="12" y1="16" x2="12.01" y2="16"></line>
                        </svg>
                        <span>{{ errorMessage }}</span>
                        <button class="alert-close" @click="errorMessage = ''">
                          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                            <line x1="18" y1="6" x2="6" y2="18"></line>
                            <line x1="6" y1="6" x2="18" y2="18"></line>
                          </svg>
                        </button>
                      </div>

                      <button
                          @click="searchProperty"
                          class="btn btn-primary btn-block btn-lg"
                          :disabled="!canSearch || loading"
                      >
                        <div v-if="loading" class="spinner white"></div>
                        <svg v-else width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                          <circle cx="11" cy="11" r="8"></circle>
                          <line x1="21" y1="21" x2="16.65" y2="16.65"></line>
                        </svg>
                        <span>{{ loading ? 'Searching...' : 'Search Property' }}</span>
                      </button>
                    </div>
                  </div>
                </div>

                <!-- Step 2: Verification -->
                <div v-else-if="currentStep === 2" key="step2" class="form-card">
                  <div class="form-card-header">
                    <button class="back-btn" @click="currentStep = 1">
                      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                        <line x1="19" y1="12" x2="5" y2="12"></line>
                        <polyline points="12 19 5 12 12 5"></polyline>
                      </svg>
                      Back
                    </button>
                    <div class="step-badge">
                      <span class="step-number">2</span>
                      <span class="step-text">Verify Identity</span>
                    </div>
                  </div>

                  <div class="form-card-body">
                    <!-- Property Found Card -->
                    <div class="property-found-card">
                      <div class="property-icon">
                        <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5">
                          <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"></path>
                          <polyline points="9 22 9 12 15 12 15 22"></polyline>
                        </svg>
                      </div>
                      <div class="property-info">
                        <span class="property-label">Property Found</span>
                        <h3 class="property-plot">{{ selectedProperty?.plotNumber }}</h3>
                        <span class="property-location">{{ selectedProperty?.location }}</span>
                      </div>
                      <div class="property-owner">
                        <span class="owner-label">Registered Owner</span>
                        <strong>{{ selectedProperty?.ownerName }}</strong>
                      </div>
                    </div>

                    <p class="form-intro">
                      To proceed with updates, please verify your identity by entering a One-Time Password (OTP) sent to your registered phone number.
                    </p>

                    <div class="otp-verification">
                      <div class="otp-sent-info">
                        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                          <rect x="5" y="2" width="14" height="20" rx="2" ry="2"></rect>
                          <line x1="12" y1="18" x2="12.01" y2="18"></line>
                        </svg>
                        <div>
                          <strong>OTP sent to {{ maskedPhone }}</strong>
                          <p>Enter the 6-digit code to verify your identity</p>
                        </div>
                      </div>

                      <div class="form-group">
                        <label class="form-label">Enter OTP Code</label>
                        <div class="otp-input-group">
                          <input
                              v-for="(digit, index) in 6"
                              :key="index"
                              v-model="otpDigits[index]"
                              type="text"
                              maxlength="1"
                              class="otp-input"
                              @input="handleOtpInput(index, $event)"
                              @keydown="handleOtpKeydown(index, $event)"
                              :ref="`otpInput${index}`"
                          />
                        </div>
                      </div>

                      <div class="resend-otp">
                        <span>Didn't receive the code?</span>
                        <button
                            @click="resendOtp"
                            class="resend-btn"
                            :disabled="resendCountdown > 0"
                        >
                          {{ resendCountdown > 0 ? `Resend in ${resendCountdown}s` : 'Resend OTP' }}
                        </button>
                      </div>

                      <!-- Error Message -->
                      <div v-if="errorMessage" class="alert alert-error">
                        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                          <circle cx="12" cy="12" r="10"></circle>
                          <line x1="12" y1="8" x2="12" y2="12"></line>
                          <line x1="12" y1="16" x2="12.01" y2="16"></line>
                        </svg>
                        <span>{{ errorMessage }}</span>
                      </div>

                      <button
                          @click="verifyOtp"
                          class="btn btn-primary btn-block btn-lg"
                          :disabled="otpCode.length !== 6 || loading"
                      >
                        <div v-if="loading" class="spinner white"></div>
                        <svg v-else width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                          <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"></path>
                        </svg>
                        <span>{{ loading ? 'Verifying...' : 'Verify & Continue' }}</span>
                      </button>
                    </div>
                  </div>
                </div>

                <!-- Step 3: Update Form -->
                <div v-else-if="currentStep === 3" key="step3" class="form-card">
                  <div class="form-card-header">
                    <button class="back-btn" @click="currentStep = 2">
                      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                        <line x1="19" y1="12" x2="5" y2="12"></line>
                        <polyline points="12 19 5 12 12 5"></polyline>
                      </svg>
                      Back
                    </button>
                    <div class="step-badge">
                      <span class="step-number">3</span>
                      <span class="step-text">Update Information</span>
                    </div>
                  </div>

                  <div class="form-card-body">
                    <!-- Verified Badge -->
                    <div class="verified-badge">
                      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                        <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"></path>
                        <polyline points="22 4 12 14.01 9 11.01"></polyline>
                      </svg>
                      <span>Identity Verified - {{ verifiedOwner?.name }}</span>
                    </div>

                    <!-- Current Property Details -->
                    <div class="current-details-section">
                      <h4 class="section-title">
                        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                          <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"></path>
                        </svg>
                        Current Property Details
                      </h4>
                      <div class="details-grid">
                        <div class="detail-item">
                          <span class="detail-label">Plot Number</span>
                          <span class="detail-value">{{ selectedProperty?.plotNumber }}</span>
                        </div>
                        <div class="detail-item">
                          <span class="detail-label">Location</span>
                          <span class="detail-value">{{ selectedProperty?.location }}</span>
                        </div>
                        <div class="detail-item">
                          <span class="detail-label">Owner</span>
                          <span class="detail-value">{{ selectedProperty?.ownerName }}</span>
                        </div>
                        <div class="detail-item">
                          <span class="detail-label">Land Value</span>
                          <span class="detail-value">KES {{ formatNumber(selectedProperty?.landValue) }}</span>
                        </div>
                      </div>
                    </div>

                    <!-- Update Type Selection -->
                    <div class="update-type-section">
                      <h4 class="section-title">
                        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                          <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"></path>
                          <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"></path>
                        </svg>
                        What would you like to update?
                      </h4>
                      <div class="update-type-grid">
                        <label
                            v-for="type in updateTypes"
                            :key="type.id"
                            class="update-type-card"
                            :class="{ selected: updateData.updateType === type.id }"
                        >
                          <input
                              type="radio"
                              v-model="updateData.updateType"
                              :value="type.id"
                              class="hidden-radio"
                          />
                          <div class="type-icon" v-html="type.icon"></div>
                          <strong>{{ type.title }}</strong>
                          <span>{{ type.description }}</span>
                        </label>
                      </div>
                    </div>

                    <!-- Dynamic Update Fields -->
                    <div v-if="updateData.updateType" class="update-fields-section">
                      <h4 class="section-title">
                        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                          <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"></path>
                          <polyline points="14 2 14 8 20 8"></polyline>
                          <line x1="16" y1="13" x2="8" y2="13"></line>
                          <line x1="16" y1="17" x2="8" y2="17"></line>
                        </svg>
                        Enter New Details
                      </h4>

                      <!-- Ownership Transfer Fields -->
                      <div v-if="updateData.updateType === 'ownership'" class="form-fields">
                        <div class="form-row">
                          <div class="form-group">
                            <label class="form-label">New Owner Name</label>
                            <input v-model="updateData.newOwnerName" type="text" class="form-input" placeholder="Full name" />
                          </div>
                          <div class="form-group">
                            <label class="form-label">New Owner ID Number</label>
                            <input v-model="updateData.newOwnerIdNo" type="text" class="form-input" placeholder="ID Number" />
                          </div>
                        </div>
                        <div class="form-row">
                          <div class="form-group">
                            <label class="form-label">New Owner Phone</label>
                            <input v-model="updateData.newOwnerPhone" type="tel" class="form-input" placeholder="Phone number" />
                          </div>
                          <div class="form-group">
                            <label class="form-label">New Owner Email</label>
                            <input v-model="updateData.newOwnerEmail" type="email" class="form-input" placeholder="Email address" />
                          </div>
                        </div>
                      </div>

                      <!-- Address Update Fields -->
                      <div v-else-if="updateData.updateType === 'address'" class="form-fields">
                        <div class="form-group">
                          <label class="form-label">New Postal Address</label>
                          <input v-model="updateData.newAddress" type="text" class="form-input" placeholder="P.O. Box" />
                        </div>
                        <div class="form-row">
                          <div class="form-group">
                            <label class="form-label">City/Town</label>
                            <input v-model="updateData.city" type="text" class="form-input" placeholder="City/Town" />
                          </div>
                          <div class="form-group">
                            <label class="form-label">Postal Code</label>
                            <input v-model="updateData.postalCode" type="text" class="form-input" placeholder="Postal Code" />
                          </div>
                        </div>
                      </div>

                      <!-- Contact Update Fields -->
                      <div v-else-if="updateData.updateType === 'contact'" class="form-fields">
                        <div class="form-row">
                          <div class="form-group">
                            <label class="form-label">New Phone Number</label>
                            <input v-model="updateData.newPhone" type="tel" class="form-input" placeholder="Phone number" />
                          </div>
                          <div class="form-group">
                            <label class="form-label">New Email Address</label>
                            <input v-model="updateData.newEmail" type="email" class="form-input" placeholder="Email address" />
                          </div>
                        </div>
                      </div>

                      <!-- Document Upload -->
                      <div class="document-upload-section">
                        <h5>Supporting Documents</h5>
                        <div class="upload-area" @click="triggerFileInput" @dragover.prevent @drop.prevent="handleFileDrop">
                          <input
                              type="file"
                              ref="fileInput"
                              @change="handleFileSelect"
                              multiple
                              accept=".pdf,.jpg,.jpeg,.png"
                              class="hidden-input"
                          />
                          <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5">
                            <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"></path>
                            <polyline points="17 8 12 3 7 8"></polyline>
                            <line x1="12" y1="3" x2="12" y2="15"></line>
                          </svg>
                          <p>Drag & drop files here or <span>browse</span></p>
                          <small>PDF, JPG, PNG • Max 5MB per file</small>
                        </div>

                        <!-- Uploaded Files List -->
                        <div v-if="uploadedFiles.length > 0" class="uploaded-files">
                          <div v-for="(file, index) in uploadedFiles" :key="index" class="file-item">
                            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                              <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"></path>
                              <polyline points="14 2 14 8 20 8"></polyline>
                            </svg>
                            <span class="file-name">{{ file.name }}</span>
                            <span class="file-size">{{ formatFileSize(file.size) }}</span>
                            <button class="remove-file" @click="removeFile(index)">
                              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                                <line x1="18" y1="6" x2="6" y2="18"></line>
                                <line x1="6" y1="6" x2="18" y2="18"></line>
                              </svg>
                            </button>
                          </div>
                        </div>
                      </div>

                      <!-- Reason for Update -->
                      <div class="form-group">
                        <label class="form-label">Reason for Update</label>
                        <textarea
                            v-model="updateData.reason"
                            class="form-textarea"
                            rows="3"
                            placeholder="Please provide a brief reason for this update request..."
                        ></textarea>
                      </div>
                    </div>

                    <button
                        @click="submitUpdate"
                        class="btn btn-success btn-block btn-lg"
                        :disabled="!canSubmit || loading"
                    >
                      <div v-if="loading" class="spinner white"></div>
                      <svg v-else width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                        <polyline points="9 11 12 14 22 4"></polyline>
                        <path d="M21 12v7a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11"></path>
                      </svg>
                      <span>{{ loading ? 'Submitting...' : 'Submit Update Request' }}</span>
                    </button>
                  </div>
                </div>

                <!-- Step 4: Success -->
                <div v-else-if="currentStep === 4" key="step4" class="form-card success-card">
                  <div class="form-card-body">
                    <div class="success-content">
                      <div class="success-icon">
                        <svg width="64" height="64" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                          <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"></path>
                          <polyline points="22 4 12 14.01 9 11.01"></polyline>
                        </svg>
                      </div>
                      <h2>Request Submitted Successfully!</h2>
                      <p class="success-message">Your property update request has been submitted for review.</p>

                      <div class="reference-card">
                        <span class="ref-label">Reference Number</span>
                        <strong class="ref-number">{{ submissionData?.referenceNumber }}</strong>
                        <p class="ref-note">Please save this number for tracking your request</p>
                      </div>

                      <div class="submission-summary">
                        <div class="summary-row">
                          <span>Property</span>
                          <strong>{{ selectedProperty?.plotNumber }}</strong>
                        </div>
                        <div class="summary-row">
                          <span>Update Type</span>
                          <strong>{{ getUpdateTypeLabel(updateData.updateType) }}</strong>
                        </div>
                        <div class="summary-row">
                          <span>Submitted</span>
                          <strong>{{ currentDate }}</strong>
                        </div>
                        <div class="summary-row">
                          <span>Expected Processing</span>
                          <strong>3-5 Business Days</strong>
                        </div>
                      </div>

                      <div class="next-steps">
                        <h4>What's Next?</h4>
                        <ul>
                          <li>
                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                              <polyline points="20 6 9 17 4 12"></polyline>
                            </svg>
                            You'll receive SMS and email updates on your request status
                          </li>
                          <li>
                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                              <polyline points="20 6 9 17 4 12"></polyline>
                            </svg>
                            Our team will review your submission within 3-5 business days
                          </li>
                          <li>
                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                              <polyline points="20 6 9 17 4 12"></polyline>
                            </svg>
                            You may be contacted for additional documents if required
                          </li>
                        </ul>
                      </div>

                      <div class="success-actions">
                        <button @click="resetProcess" class="btn btn-primary">
                          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                            <line x1="12" y1="5" x2="12" y2="19"></line>
                            <line x1="5" y1="12" x2="19" y2="12"></line>
                          </svg>
                          Submit Another Request
                        </button>
                        <button @click="goHome" class="btn btn-secondary">
                          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                            <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"></path>
                          </svg>
                          Back to Home
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              </transition>
            </div>
          </div>
        </div>
      </section>
    </main>

    <!-- Loading Overlay -->
    <transition name="fade">
      <div v-if="loading && loadingMessage" class="loading-overlay">
        <div class="loading-content">
          <div class="loader-ring large"></div>
          <p>{{ loadingMessage }}</p>
        </div>
      </div>
    </transition>

    <FooterComponent />
  </div>
</template>

<script>
import { authUrl, biller, execute } from "@/api";
import HeaderComponent from "@/components/HeaderComponent.vue";
import FooterComponent from "@/components/FooterComponent.vue";

export default {
  name: "PropertySelfService",
  components: { HeaderComponent, FooterComponent },
  data() {
    return {
      // State
      currentStep: 1,
      loading: false,
      loadingMessage: '',
      errorMessage: '',

      // Steps
      steps: ['Search Property', 'Verify Identity', 'Update Info', 'Submit'],

      // Search
      searchMethod: 'plotNumber',
      searchData: {
        subCounty: null,
        ward: null,
        plotNumber: '',
        idNumber: '',
        ownerName: '',
        phoneNumber: ''
      },

      // Location data
      subCounties: [],
      wards: [],

      // Property data
      selectedProperty: null,
      verifiedOwner: null,

      // OTP
      otpDigits: ['', '', '', '', '', ''],
      resendCountdown: 0,

      // Update data
      updateTypes: [
        {
          id: 'ownership',
          title: 'Ownership Transfer',
          description: 'Transfer property to new owner',
          icon: '<svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M16 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"></path><circle cx="8.5" cy="7" r="4"></circle><polyline points="17 11 19 13 23 9"></polyline></svg>'
        },
        {
          id: 'address',
          title: 'Address Update',
          description: 'Update postal/physical address',
          icon: '<svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"></path><circle cx="12" cy="10" r="3"></circle></svg>'
        },
        {
          id: 'contact',
          title: 'Contact Details',
          description: 'Update phone number/email',
          icon: '<svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72"></path></svg>'
        }
      ],
      updateData: {
        updateType: null,
        newOwnerName: '',
        newOwnerIdNo: '',
        newOwnerPhone: '',
        newOwnerEmail: '',
        newAddress: '',
        city: '',
        postalCode: '',
        newPhone: '',
        newEmail: '',
        reason: ''
      },
      uploadedFiles: [],

      // Submission
      submissionData: null,

      // County
      county: null
    };
  },

  computed: {
    canSearch() {
      if (this.searchMethod === 'plotNumber') {
        return this.searchData.subCounty && this.searchData.ward && this.searchData.plotNumber;
      } else {
        return this.searchData.idNumber || (this.searchData.ownerName && this.searchData.phoneNumber);
      }
    },

    otpCode() {
      return this.otpDigits.join('');
    },

    maskedPhone() {
      const phone = this.selectedProperty?.phoneNumber || '0712345678';
      return phone.substring(0, 4) + '****' + phone.substring(phone.length - 2);
    },

    canSubmit() {
      if (!this.updateData.updateType) return false;
      if (this.uploadedFiles.length === 0) return false;

      switch (this.updateData.updateType) {
        case 'ownership':
          return this.updateData.newOwnerName && this.updateData.newOwnerIdNo;
        case 'address':
          return this.updateData.newAddress && this.updateData.city;
        case 'contact':
          return this.updateData.newPhone || this.updateData.newEmail;
        default:
          return false;
      }
    },

    currentDate() {
      return new Date().toLocaleDateString('en-KE', {
        year: 'numeric',
        month: 'long',
        day: 'numeric'
      });
    }
  },

  watch: {
    'searchData.subCounty'(val) {
      if (val) {
        this.getWards(val.subCountyID);
      } else {
        this.wards = [];
      }
      this.searchData.ward = null;
    }
  },

  mounted() {
    this.getCounty();
    this.getSubCounties();
  },

  methods: {
    formatNumber(num) {
      if (!num) return '0';
      return new Intl.NumberFormat('en-KE').format(num);
    },

    formatFileSize(bytes) {
      if (bytes === 0) return '0 Bytes';
      const k = 1024;
      const sizes = ['Bytes', 'KB', 'MB', 'GB'];
      const i = Math.floor(Math.log(bytes) / Math.log(k));
      return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
    },

    getUpdateTypeLabel(typeId) {
      const type = this.updateTypes.find(t => t.id === typeId);
      return type ? type.title : '';
    },

    goHome() {
      this.$router.push('/');
    },

    resetProcess() {
      this.currentStep = 1;
      this.selectedProperty = null;
      this.verifiedOwner = null;
      this.submissionData = null;
      this.errorMessage = '';
      this.searchData = {
        subCounty: null,
        ward: null,
        plotNumber: '',
        idNumber: '',
        ownerName: '',
        phoneNumber: ''
      };
      this.otpDigits = ['', '', '', '', '', ''];
      this.updateData = {
        updateType: null,
        newOwnerName: '',
        newOwnerIdNo: '',
        newOwnerPhone: '',
        newOwnerEmail: '',
        newAddress: '',
        city: '',
        postalCode: '',
        newPhone: '',
        newEmail: '',
        reason: ''
      };
      this.uploadedFiles = [];
    },

    // API Methods
    async getCounty() {
      try {
        const data = new FormData();
        data.append("function", "getCounty");
        const res = await execute(data, authUrl);
        if (res.data.success) {
          this.county = res.data.data.county;
        }
      } catch (error) {
        console.error('Error fetching county:', error);
      }
    },

    async getSubCounties() {
      try {
        const data = new FormData();
        data.append("function", "getSubCounty");
        const res = await execute(data, biller);
        if (res.data.success) {
          this.subCounties = res.data.data.subCounties;
        }
      } catch (error) {
        console.error('Error fetching sub-counties:', error);
      }
    },

    async getWards(subCountyID) {
      try {
        const data = new FormData();
        data.append("function", "getWards");
        data.append("subCountyID", subCountyID);
        const res = await execute(data, biller);
        if (res.data.success) {
          this.wards = res.data.data.wards;
        }
      } catch (error) {
        console.error('Error fetching wards:', error);
      }
    },

    async searchProperty() {
      this.errorMessage = '';
      this.loading = true;
      this.loadingMessage = 'Searching for property...';

      try {
        const data = new FormData();
        data.append("function", "searchProperty");

        if (this.searchMethod === 'plotNumber') {
          data.append("plotNumber", this.searchData.plotNumber);
          data.append("subCountyID", this.searchData.subCounty.subCountyID);
          data.append("wardID", this.searchData.ward.wardID);
        } else {
          data.append("idNumber", this.searchData.idNumber);
          data.append("ownerName", this.searchData.ownerName);
          data.append("phoneNumber", this.searchData.phoneNumber);
        }

        const res = await execute(data, biller);

        if (res.data.success) {
          this.selectedProperty = res.data.data.property;
          this.currentStep = 2;
          this.startResendCountdown();
        } else {
          this.errorMessage = res.data.message || 'Property not found. Please check your details.';
        }
      } catch (error) {
        this.errorMessage = 'An error occurred while searching. Please try again.';
      } finally {
        this.loading = false;
        this.loadingMessage = '';
      }
    },

    // OTP Handling
    handleOtpInput(index, event) {
      const value = event.target.value;
      if (value && index < 5) {
        this.$refs[`otpInput${index + 1}`][0].focus();
      }
    },

    handleOtpKeydown(index, event) {
      if (event.key === 'Backspace' && !this.otpDigits[index] && index > 0) {
        this.$refs[`otpInput${index - 1}`][0].focus();
      }
    },

    startResendCountdown() {
      this.resendCountdown = 60;
      const timer = setInterval(() => {
        this.resendCountdown--;
        if (this.resendCountdown <= 0) {
          clearInterval(timer);
        }
      }, 1000);
    },

    async resendOtp() {
      this.loading = true;
      // Simulate OTP resend
      setTimeout(() => {
        this.loading = false;
        this.startResendCountdown();
      }, 1000);
    },

    async verifyOtp() {
      this.errorMessage = '';
      this.loading = true;
      this.loadingMessage = 'Verifying identity...';

      try {
        // Simulate verification
        setTimeout(() => {
          this.verifiedOwner = {
            name: this.selectedProperty?.ownerName,
            idNo: this.searchData.idNumber
          };
          this.currentStep = 3;
          this.loading = false;
          this.loadingMessage = '';
        }, 1500);
      } catch (error) {
        this.errorMessage = 'Verification failed. Please try again.';
        this.loading = false;
        this.loadingMessage = '';
      }
    },

    // File Handling
    triggerFileInput() {
      this.$refs.fileInput.click();
    },

    handleFileSelect(event) {
      const files = Array.from(event.target.files);
      this.processFiles(files);
    },

    handleFileDrop(event) {
      const files = Array.from(event.dataTransfer.files);
      this.processFiles(files);
    },

    processFiles(files) {
      const validFiles = files.filter(file => {
        const validTypes = ['application/pdf', 'image/jpeg', 'image/png', 'image/jpg'];
        const maxSize = 5 * 1024 * 1024; // 5MB
        return validTypes.includes(file.type) && file.size <= maxSize;
      });
      this.uploadedFiles = [...this.uploadedFiles, ...validFiles];
    },

    removeFile(index) {
      this.uploadedFiles.splice(index, 1);
    },

    async submitUpdate() {
      this.loading = true;
      this.loadingMessage = 'Submitting your request...';

      try {
        // Simulate submission
        setTimeout(() => {
          this.submissionData = {
            referenceNumber: 'PRU-' + Date.now().toString().slice(-8),
            submittedAt: new Date().toISOString()
          };
          this.currentStep = 4;
          this.loading = false;
          this.loadingMessage = '';
        }, 2000);
      } catch (error) {
        this.errorMessage = 'Submission failed. Please try again.';
        this.loading = false;
        this.loadingMessage = '';
      }
    }
  }
};
</script>

<style scoped>
@import url('https://fonts.googleapis.com/css2?family=DM+Sans:wght@400;500;600;700&family=Plus+Jakarta+Sans:wght@400;500;600;700;800&display=swap');

.page-wrapper {
  min-height: 100vh;
  display: flex;
  flex-direction: column;
  background: #f8fafc;
}

.main-content {
  flex: 1;
  padding-top: 72px;
}

/* Page Hero */
.page-hero {
  background: linear-gradient(135deg, #0f4c81 0%, #0a3459 100%);
  padding: 48px 0 60px;
  color: white;
}

.container {
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 24px;
}

.breadcrumb-nav {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 16px;
  font-size: 14px;
}

.breadcrumb-link {
  color: rgba(255,255,255,0.7);
  text-decoration: none;
  transition: color 0.2s;
}

.breadcrumb-link:hover { color: white; }
.breadcrumb-separator { color: rgba(255,255,255,0.4); }
.breadcrumb-current { color: white; }

.page-title {
  font-family: 'Plus Jakarta Sans', sans-serif;
  font-size: 32px;
  font-weight: 800;
  margin-bottom: 8px;
}

.page-subtitle {
  opacity: 0.9;
  font-size: 16px;
  margin-bottom: 32px;
}

/* Progress Stepper */
.progress-stepper {
  display: flex;
  justify-content: center;
  gap: 0;
  background: rgba(255,255,255,0.1);
  backdrop-filter: blur(10px);
  border-radius: 16px;
  padding: 24px 32px;
}

.step-item {
  display: flex;
  align-items: center;
  gap: 12px;
}

.step-connector {
  width: 60px;
  height: 3px;
  background: rgba(255,255,255,0.3);
  margin: 0 8px;
  border-radius: 2px;
}

.step-item.completed .step-connector {
  background: #10b981;
}

.step-circle {
  width: 40px;
  height: 40px;
  border-radius: 50%;
  background: rgba(255,255,255,0.2);
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 700;
  font-size: 14px;
  transition: all 0.3s;
}

.step-item.active .step-circle {
  background: white;
  color: #0f4c81;
  transform: scale(1.1);
  box-shadow: 0 4px 12px rgba(0,0,0,0.2);
}

.step-item.completed .step-circle {
  background: #10b981;
  color: white;
}

.step-label {
  font-size: 14px;
  font-weight: 500;
  opacity: 0.8;
}

.step-item.active .step-label,
.step-item.completed .step-label {
  opacity: 1;
  font-weight: 600;
}

/* Content Section */
.content-section {
  padding: 48px 0 80px;
}

.content-grid {
  display: grid;
  grid-template-columns: 1fr 1.3fr;
  gap: 32px;
  align-items: start;
}

/* Info Panel */
.info-panel {
  position: sticky;
  top: 96px;
}

.info-card {
  background: white;
  border-radius: 16px;
  padding: 32px;
  box-shadow: 0 4px 6px -1px rgba(0,0,0,0.1);
}

.info-card-header {
  display: flex;
  align-items: center;
  gap: 16px;
  margin-bottom: 20px;
}

.info-icon {
  width: 56px;
  height: 56px;
  background: linear-gradient(135deg, #dbeafe 0%, #bfdbfe 100%);
  border-radius: 14px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #0f4c81;
}

.info-card-header h3 {
  font-family: 'Plus Jakarta Sans', sans-serif;
  font-size: 22px;
  font-weight: 700;
  color: #1e293b;
}

.info-description {
  color: #64748b;
  line-height: 1.6;
  margin-bottom: 24px;
}

.info-features {
  display: flex;
  flex-direction: column;
  gap: 16px;
  margin-bottom: 24px;
}

.feature-item {
  display: flex;
  gap: 12px;
  padding: 16px;
  background: #f8fafc;
  border-radius: 12px;
}

.feature-icon {
  width: 40px;
  height: 40px;
  border-radius: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.feature-icon.green { background: #d1fae5; color: #059669; }
.feature-icon.blue { background: #dbeafe; color: #2563eb; }
.feature-icon.purple { background: #dbeafe; color: #0f4c81; }

.feature-item strong {
  display: block;
  font-size: 14px;
  color: #1e293b;
  margin-bottom: 2px;
}

.feature-item p {
  font-size: 13px;
  color: #64748b;
  margin: 0;
}

/* Required Documents */
.required-docs {
  padding: 20px;
  background: #fef3c7;
  border: 1px solid #fcd34d;
  border-radius: 12px;
  margin-bottom: 20px;
}

.required-docs h4 {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 14px;
  font-weight: 600;
  color: #92400e;
  margin-bottom: 12px;
}

.required-docs ul {
  list-style: none;
  padding: 0;
  margin: 0 0 12px;
}

.required-docs li {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 13px;
  color: #78350f;
  margin-bottom: 8px;
}

.required-docs li svg {
  color: #059669;
}

.docs-note {
  font-size: 12px;
  color: #a16207;
  margin: 0;
}

/* Help Contact */
.help-contact {
  padding: 20px;
  background: #eff6ff;
  border: 1px solid #bfdbfe;
  border-radius: 12px;
}

.help-contact h4 {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 14px;
  font-weight: 600;
  color: #1e40af;
  margin-bottom: 12px;
}

.contact-info {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.contact-item {
  display: flex;
  align-items: center;
  gap: 10px;
  font-size: 14px;
  color: #3b82f6;
}

/* Form Panel */
.form-card {
  background: white;
  border-radius: 16px;
  box-shadow: 0 4px 6px -1px rgba(0,0,0,0.1);
  overflow: hidden;
}

.form-card-header {
  padding: 24px 32px;
  background: #f8fafc;
  border-bottom: 1px solid #e2e8f0;
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.step-badge {
  display: flex;
  align-items: center;
  gap: 10px;
}

.step-badge .step-number {
  width: 28px;
  height: 28px;
  background: #0f4c81;
  color: white;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 600;
  font-size: 13px;
}

.step-badge .step-text {
  font-weight: 600;
  color: #1e293b;
}

.back-btn {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 16px;
  background: none;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  color: #64748b;
  font-size: 14px;
  cursor: pointer;
  transition: all 0.2s;
}

.back-btn:hover {
  background: #f1f5f9;
  color: #1e293b;
}

.form-card-body {
  padding: 32px;
}

.form-intro {
  color: #64748b;
  margin-bottom: 24px;
  line-height: 1.6;
}

/* Search Method Toggle */
.search-method-toggle {
  display: flex;
  gap: 12px;
  margin-bottom: 24px;
}

.toggle-btn {
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  padding: 14px 20px;
  background: #f8fafc;
  border: 2px solid #e2e8f0;
  border-radius: 10px;
  font-size: 14px;
  font-weight: 600;
  color: #64748b;
  cursor: pointer;
  transition: all 0.2s;
}

.toggle-btn:hover {
  border-color: #0f4c81;
  color: #0f4c81;
}

.toggle-btn.active {
  background: #0f4c81;
  border-color: #0f4c81;
  color: white;
}

/* Form Elements */
.form-group {
  margin-bottom: 20px;
}

.form-row {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 16px;
}

.form-label {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 14px;
  font-weight: 600;
  color: #1e293b;
  margin-bottom: 10px;
}

.select-wrapper {
  position: relative;
}

.form-select {
  width: 100%;
  padding: 14px 44px 14px 16px;
  border: 2px solid #e2e8f0;
  border-radius: 10px;
  font-family: 'DM Sans', sans-serif;
  font-size: 15px;
  background: white;
  cursor: pointer;
  appearance: none;
  transition: all 0.2s;
}

.form-select:focus {
  outline: none;
  border-color: #0f4c81;
  box-shadow: 0 0 0 4px rgba(15, 76, 129, 0.1);
}

.form-select:disabled {
  background: #f8fafc;
  cursor: not-allowed;
}

.select-icon {
  position: absolute;
  right: 14px;
  top: 50%;
  transform: translateY(-50%);
  color: #94a3b8;
  pointer-events: none;
}

.input-with-icon {
  position: relative;
}

.form-input {
  width: 100%;
  padding: 14px 16px;
  padding-right: 48px;
  border: 2px solid #e2e8f0;
  border-radius: 10px;
  font-family: 'DM Sans', sans-serif;
  font-size: 15px;
  transition: all 0.2s;
  background: white;
}

.form-input:focus {
  outline: none;
  border-color: #0f4c81;
  box-shadow: 0 0 0 4px rgba(15, 76, 129, 0.1);
}

.input-icon {
  position: absolute;
  right: 14px;
  top: 50%;
  transform: translateY(-50%);
  color: #94a3b8;
}

.form-textarea {
  width: 100%;
  padding: 14px 16px;
  border: 2px solid #e2e8f0;
  border-radius: 10px;
  font-family: 'DM Sans', sans-serif;
  font-size: 15px;
  resize: vertical;
  transition: all 0.2s;
}

.form-textarea:focus {
  outline: none;
  border-color: #0f4c81;
  box-shadow: 0 0 0 4px rgba(15, 76, 129, 0.1);
}

/* Alert */
.alert {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 14px 16px;
  border-radius: 10px;
  margin-bottom: 20px;
  font-size: 14px;
}

.alert-error {
  background: #fef2f2;
  border: 1px solid #fecaca;
  color: #dc2626;
}

.alert-close {
  margin-left: auto;
  background: none;
  border: none;
  cursor: pointer;
  padding: 4px;
  color: inherit;
}

/* Buttons */
.btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  padding: 14px 24px;
  border-radius: 10px;
  font-family: 'DM Sans', sans-serif;
  font-size: 15px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;
  border: none;
}

.btn-block { width: 100%; }
.btn-lg { padding: 16px 32px; font-size: 16px; }

.btn-primary {
  background: #0f4c81;
  color: white;
}

.btn-primary:hover:not(:disabled) {
  background: #0a3459;
}

.btn-success {
  background: #059669;
  color: white;
}

.btn-success:hover:not(:disabled) {
  background: #047857;
}

.btn-secondary {
  background: #f1f5f9;
  color: #475569;
  border: 1px solid #e2e8f0;
}

.btn-secondary:hover {
  background: #e2e8f0;
}

.btn:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

/* Spinner */
.spinner {
  width: 20px;
  height: 20px;
  border: 2px solid rgba(0,0,0,0.1);
  border-top-color: currentColor;
  border-radius: 50%;
  animation: spin 0.8s linear infinite;
}

.spinner.white {
  border-color: rgba(255,255,255,0.3);
  border-top-color: white;
}

@keyframes spin {
  to { transform: rotate(360deg); }
}

/* Property Found Card */
.property-found-card {
  display: flex;
  align-items: center;
  gap: 16px;
  padding: 24px;
  background: linear-gradient(135deg, #dbeafe 0%, #bfdbfe 100%);
  border-radius: 14px;
  margin-bottom: 24px;
}

.property-icon {
  width: 64px;
  height: 64px;
  background: white;
  border-radius: 14px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #0f4c81;
  box-shadow: 0 4px 6px -1px rgba(0,0,0,0.1);
}

.property-info {
  flex: 1;
}

.property-label {
  font-size: 12px;
  color: #6b7280;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.property-plot {
  font-family: 'Plus Jakarta Sans', sans-serif;
  font-size: 20px;
  font-weight: 700;
  color: #1e293b;
  margin: 4px 0;
}

.property-location {
  font-size: 14px;
  color: #64748b;
}

.property-owner {
  text-align: right;
}

.owner-label {
  display: block;
  font-size: 11px;
  color: #6b7280;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  margin-bottom: 4px;
}

.property-owner strong {
  color: #1e293b;
  font-size: 14px;
}

/* OTP Verification */
.otp-verification {
  margin-top: 24px;
}

.otp-sent-info {
  display: flex;
  align-items: flex-start;
  gap: 16px;
  padding: 20px;
  background: #eff6ff;
  border-radius: 12px;
  margin-bottom: 24px;
}

.otp-sent-info svg {
  color: #2563eb;
  flex-shrink: 0;
}

.otp-sent-info strong {
  display: block;
  color: #1e40af;
  margin-bottom: 4px;
}

.otp-sent-info p {
  margin: 0;
  font-size: 13px;
  color: #3b82f6;
}

.otp-input-group {
  display: flex;
  justify-content: center;
  gap: 12px;
  margin-bottom: 20px;
}

.otp-input {
  width: 50px;
  height: 56px;
  text-align: center;
  font-size: 24px;
  font-weight: 700;
  border: 2px solid #e2e8f0;
  border-radius: 10px;
  transition: all 0.2s;
}

.otp-input:focus {
  outline: none;
  border-color: #0f4c81;
  box-shadow: 0 0 0 4px rgba(15, 76, 129, 0.1);
}

.resend-otp {
  text-align: center;
  margin-bottom: 24px;
  font-size: 14px;
  color: #64748b;
}

.resend-btn {
  background: none;
  border: none;
  color: #0f4c81;
  font-weight: 600;
  cursor: pointer;
  margin-left: 8px;
}

.resend-btn:disabled {
  color: #94a3b8;
  cursor: not-allowed;
}

/* Verified Badge */
.verified-badge {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 12px 16px;
  background: #d1fae5;
  border: 1px solid #6ee7b7;
  border-radius: 10px;
  color: #047857;
  font-size: 14px;
  font-weight: 600;
  margin-bottom: 24px;
}

/* Section Titles */
.section-title {
  display: flex;
  align-items: center;
  gap: 10px;
  font-family: 'Plus Jakarta Sans', sans-serif;
  font-size: 16px;
  font-weight: 600;
  color: #1e293b;
  margin-bottom: 16px;
}

/* Current Details */
.current-details-section {
  margin-bottom: 24px;
}

.details-grid {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 12px;
}

.detail-item {
  padding: 14px;
  background: #f8fafc;
  border-radius: 10px;
}

.detail-label {
  display: block;
  font-size: 11px;
  color: #64748b;
  margin-bottom: 4px;
  text-transform: uppercase;
  font-weight: 500;
  letter-spacing: 0.5px;
}

.detail-value {
  font-size: 14px;
  font-weight: 600;
  color: #1e293b;
}

/* Update Type Selection */
.update-type-section {
  margin-bottom: 24px;
}

.update-type-grid {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 12px;
}

.update-type-card {
  padding: 20px 16px;
  background: #f8fafc;
  border: 2px solid #e2e8f0;
  border-radius: 12px;
  text-align: center;
  cursor: pointer;
  transition: all 0.2s;
}

.update-type-card:hover {
  border-color: #0f4c81;
}

.update-type-card.selected {
  background: #dbeafe;
  border-color: #0f4c81;
}

.hidden-radio {
  display: none;
}

.type-icon {
  width: 48px;
  height: 48px;
  background: white;
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto 12px;
  color: #0f4c81;
}

.update-type-card strong {
  display: block;
  font-size: 14px;
  color: #1e293b;
  margin-bottom: 4px;
}

.update-type-card span {
  font-size: 12px;
  color: #64748b;
}

/* Update Fields */
.update-fields-section {
  padding-top: 24px;
  border-top: 1px solid #e2e8f0;
  margin-bottom: 24px;
}

.form-fields {
  margin-bottom: 24px;
}

/* Document Upload */
.document-upload-section {
  margin-bottom: 24px;
}

.document-upload-section h5 {
  font-size: 14px;
  font-weight: 600;
  color: #1e293b;
  margin-bottom: 12px;
}

.upload-area {
  border: 2px dashed #d1d5db;
  border-radius: 12px;
  padding: 40px 20px;
  text-align: center;
  cursor: pointer;
  transition: all 0.2s;
}

.upload-area:hover {
  border-color: #0f4c81;
  background: #eff6ff;
}

.hidden-input {
  display: none;
}

.upload-area svg {
  color: #9ca3af;
  margin-bottom: 12px;
}

.upload-area p {
  color: #6b7280;
  margin-bottom: 4px;
}

.upload-area p span {
  color: #0f4c81;
  font-weight: 600;
}

.upload-area small {
  color: #9ca3af;
}

.uploaded-files {
  margin-top: 16px;
}

.file-item {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px 16px;
  background: #f8fafc;
  border-radius: 8px;
  margin-bottom: 8px;
}

.file-item svg {
  color: #0f4c81;
}

.file-name {
  flex: 1;
  font-size: 14px;
  color: #1e293b;
}

.file-size {
  font-size: 12px;
  color: #64748b;
}

.remove-file {
  background: none;
  border: none;
  color: #ef4444;
  cursor: pointer;
  padding: 4px;
}

/* Success Card */
.success-card {
  text-align: center;
}

.success-content {
  max-width: 500px;
  margin: 0 auto;
}

.success-icon {
  width: 100px;
  height: 100px;
  background: #d1fae5;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #059669;
  margin: 0 auto 24px;
}

.success-content h2 {
  font-family: 'Plus Jakarta Sans', sans-serif;
  font-size: 24px;
  font-weight: 700;
  color: #059669;
  margin-bottom: 8px;
}

.success-message {
  color: #64748b;
  margin-bottom: 32px;
}

.reference-card {
  background: linear-gradient(135deg, #0f4c81 0%, #0a3459 100%);
  border-radius: 14px;
  padding: 24px;
  color: white;
  margin-bottom: 24px;
}

.ref-label {
  font-size: 12px;
  opacity: 0.8;
  text-transform: uppercase;
  letter-spacing: 1px;
}

.ref-number {
  display: block;
  font-family: 'Plus Jakarta Sans', sans-serif;
  font-size: 28px;
  font-weight: 800;
  margin: 8px 0;
  letter-spacing: 2px;
}

.ref-note {
  font-size: 13px;
  opacity: 0.8;
  margin: 0;
}

.submission-summary {
  background: #f8fafc;
  border-radius: 12px;
  padding: 20px;
  margin-bottom: 24px;
  text-align: left;
}

.summary-row {
  display: flex;
  justify-content: space-between;
  padding: 10px 0;
  border-bottom: 1px solid #e2e8f0;
  font-size: 14px;
}

.summary-row:last-child {
  border-bottom: none;
}

.summary-row span {
  color: #64748b;
}

.summary-row strong {
  color: #1e293b;
}

.next-steps {
  text-align: left;
  margin-bottom: 32px;
}

.next-steps h4 {
  font-size: 16px;
  font-weight: 600;
  color: #1e293b;
  margin-bottom: 16px;
}

.next-steps ul {
  list-style: none;
  padding: 0;
  margin: 0;
}

.next-steps li {
  display: flex;
  align-items: flex-start;
  gap: 12px;
  padding: 10px 0;
  font-size: 14px;
  color: #475569;
}

.next-steps li svg {
  color: #059669;
  flex-shrink: 0;
  margin-top: 2px;
}

.success-actions {
  display: flex;
  gap: 12px;
}

.success-actions .btn {
  flex: 1;
}

/* Loading Overlay */
.loading-overlay {
  position: fixed;
  inset: 0;
  background: rgba(0,0,0,0.7);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 2000;
}

.loading-content {
  text-align: center;
  color: white;
}

.loader-ring.large {
  width: 60px;
  height: 60px;
  border: 4px solid rgba(255,255,255,0.2);
  border-top-color: white;
  border-radius: 50%;
  margin: 0 auto 20px;
  animation: spin 1s linear infinite;
}

.loading-content p {
  font-size: 16px;
}

/* Transitions */
.slide-fade-enter-active {
  transition: all 0.3s ease;
}

.slide-fade-leave-active {
  transition: all 0.2s ease;
}

.slide-fade-enter-from {
  opacity: 0;
  transform: translateX(20px);
}

.slide-fade-leave-to {
  opacity: 0;
  transform: translateX(-20px);
}

.fade-enter-active, .fade-leave-active {
  transition: opacity 0.3s ease;
}

.fade-enter-from, .fade-leave-to {
  opacity: 0;
}

/* Responsive */
@media (max-width: 1024px) {
  .content-grid {
    grid-template-columns: 1fr;
  }

  .info-panel {
    position: static;
  }

  .progress-stepper {
    flex-wrap: wrap;
    gap: 16px;
  }

  .step-connector {
    display: none;
  }
}

@media (max-width: 768px) {
  .form-card-body {
    padding: 24px;
  }

  .form-row {
    grid-template-columns: 1fr;
  }

  .details-grid {
    grid-template-columns: 1fr;
  }

  .update-type-grid {
    grid-template-columns: 1fr;
  }

  .property-found-card {
    flex-direction: column;
    text-align: center;
  }

  .property-owner {
    text-align: center;
  }

  .otp-input-group {
    gap: 8px;
  }

  .otp-input {
    width: 44px;
    height: 50px;
    font-size: 20px;
  }

  .page-title {
    font-size: 26px;
  }

  .success-actions {
    flex-direction: column;
  }

  .search-method-toggle {
    flex-direction: column;
  }
}
</style>